<template>
  <div class="formulario-manifesto">
    <div
      v-for="grupo in gruposVisiveis"
      :key="grupo.nome"
      class="grupo-campos"
    >
      <div v-if="grupo.nome && somenteGrupo === null" class="grupo-titulo">{{ grupo.nome }}</div>

      <!--
        Liga/desliga é decisão de uma linha só: título, explicação curta e o
        interruptor à direita. Em lista — e estes campos quase sempre vêm em
        lista — isso se lê de uma passada de olho, enquanto um "Sim/Não" por
        campo obriga a abrir cada um para saber o que está valendo.
      -->
      <div
        v-for="campo in grupo.campos"
        :key="campo.chave"
        :class="campo.tipo === 'BOOLEAN' ? 'campo-interruptor' : 'form-group'"
      >
        <div v-if="campo.tipo === 'BOOLEAN'" class="campo-interruptor__texto">
          <span class="campo-interruptor__titulo">{{ campo.label || campo.chave }}</span>
          <small v-if="campo.ajuda" class="campo-interruptor__ajuda">{{ campo.ajuda }}</small>
          <small v-if="erros[campo.chave]" class="campo-erro">{{ erros[campo.chave] }}</small>
        </div>

        <label v-if="campo.tipo !== 'BOOLEAN'" :for="'campo-' + campo.chave">
          {{ campo.label || campo.chave }}
          <span v-if="campo.obrigatorio" class="obrigatorio">*</span>
        </label>

        <!--
          Escolha com busca, não lista rolante: há campo cujas opções são
          registros do sistema — tabela de preço, filial —, e aí são dezenas
          ou centenas. Procurar pelo nome é a única forma prática.
        -->
        <v-select
          v-if="campo.tipo === 'SELECT'"
          :id="'campo-' + campo.chave"
          :options="opcoesDoCampo(campo)"
          label="rotulo"
          :value="opcaoEscolhida(campo)"
          :placeholder="campo.placeholder || 'Selecione...'"
          @input="escolher(campo, $event)"
        >
          <template #no-options>
            <span v-if="carregando[campo.fonte]">Carregando...</span>
            <span v-else-if="falhouFonte[campo.fonte]" class="opcoes-falharam">
              Não foi possível carregar a lista.
              <a href="#" @click.prevent="recarregarFonte(campo.fonte)">Tentar novamente</a>
            </span>
            <span v-else>Nada encontrado</span>
          </template>
        </v-select>

        <button
          v-else-if="campo.tipo === 'BOOLEAN'"
          :id="'campo-' + campo.chave"
          type="button"
          role="switch"
          :aria-checked="ligado(campo) ? 'true' : 'false'"
          :aria-label="campo.label || campo.chave"
          class="interruptor"
          :class="{ 'interruptor--ligado': ligado(campo) }"
          @click="atualizar(campo, !ligado(campo))"
        >
          <span class="interruptor__bolinha"></span>
        </button>

        <textarea
          v-else-if="campo.tipo === 'TEXTAREA'"
          :id="'campo-' + campo.chave"
          class="form-control"
          rows="6"
          :placeholder="campo.placeholder || ''"
          :value="valorDoCampo(campo)"
          @input="atualizar(campo, $event.target.value)"
        ></textarea>

        <!--
          Quem decide o mascaramento é o backend, pelo sinalizador `secreto` —
          ele cifra e devolve mascarado por ele, não pelo tipo. Campo secreto
          de outro tipo voltaria com a máscara num input comum, e o usuário
          editaria a máscara achando que editava a credencial.
        -->
        <div v-else-if="campoSecreto(campo)" class="campo-secreto">
          <input
            :id="'campo-' + campo.chave"
            class="form-control"
            :type="segredoVisivel[campo.chave] ? 'text' : 'password'"
            :placeholder="campo.placeholder || ''"
            :value="valorDoCampo(campo)"
            @focus="limpaMascara(campo)"
            @input="atualizar(campo, $event.target.value)"
          />
          <CButton
            type="button"
            size="sm"
            variant="outline"
            color="secondary"
            :title="segredoVisivel[campo.chave] ? 'Ocultar' : 'Mostrar'"
            @click="alternarVisibilidade(campo)"
          >
            <fa-icon :icon="segredoVisivel[campo.chave] ? 'eye-slash' : 'eye'" />
          </CButton>
        </div>

        <div v-else-if="campo.tipo === 'WEBHOOK'" class="campo-webhook">
          <div v-if="enderecoExibido" class="campo-webhook__linha">
            <input
              :id="'campo-' + campo.chave"
              class="form-control"
              type="text"
              readonly
              :value="enderecoExibido"
              @focus="$event.target.select()"
            />
            <CButton
              type="button"
              size="sm"
              color="primary"
              variant="outline"
              :title="copiado ? 'Copiado' : 'Copiar endereço'"
              @click="copiarUrl"
            >
              <fa-icon :icon="copiado ? 'check' : 'copy'" />
              {{ copiado ? 'Copiado' : 'Copiar' }}
            </CButton>
          </div>

          <p v-else class="campo-webhook__pendente">
            O endereço é gerado ao salvar a integração pela primeira vez.
          </p>

          <ul v-if="eventosWebhook.length" class="campo-webhook__eventos">
            <li v-for="evento in eventosWebhook" :key="evento.chave">
              <strong>{{ evento.nome }}</strong>
              <span v-if="evento.descricao"> — {{ evento.descricao }}</span>
            </li>
          </ul>
        </div>

        <!--
          A conta do provedor não é dado que se digita: o usuário autoriza lá e
          volta. O valor gravado é o identificador da conexão, que nunca passa
          pelo formulário.
        -->
        <ConexaoHub
          v-else-if="campo.tipo === 'OAUTH_HUB'"
          :integracao-id="integracaoId"
          :conexao="conexao"
          :provedores-do-campo="campo.provedores_oauth || {}"
          :conectar-ao-abrir="conectarAoAbrir"
          :salvando="salvando"
          @desconectar="$emit('desconectar')"
          @salvar-e-conectar="$emit('salvar-e-conectar', $event)"
          @retomada-cumprida="$emit('retomada-cumprida')"
        />

        <input
          v-else
          :id="'campo-' + campo.chave"
          class="form-control"
          :type="tipoDoInput(campo)"
          :placeholder="campo.placeholder || ''"
          :value="valorDoCampo(campo)"
          @input="atualizar(campo, $event.target.value)"
        />

        <!--
          Valores prontos para clicar, quando o conector os declara. Serve a
          campo cuja sintaxe o usuário não tem como adivinhar — a máscara de
          discagem é o caso: três marcadores e o resto literal, e preencher
          errado só se descobre na primeira ligação que não completa.
        -->
        <div v-if="exemplosDoCampo(campo).length" class="campo-exemplos">
          <span class="campo-exemplos__rotulo">Exemplos:</span>
          <button
            v-for="exemplo in exemplosDoCampo(campo)"
            :key="exemplo"
            type="button"
            class="campo-exemplos__opcao"
            :class="{ 'campo-exemplos__opcao--atual': valorDoCampo(campo) === exemplo }"
            :title="'Preencher com ' + exemplo"
            @click="atualizar(campo, exemplo)"
          >
            {{ exemplo }}
          </button>
        </div>

        <small v-if="campo.ajuda && campo.tipo !== 'BOOLEAN'" class="campo-ajuda">{{ campo.ajuda }}</small>
        <small v-if="erros[campo.chave] && campo.tipo !== 'BOOLEAN'" class="campo-erro">{{ erros[campo.chave] }}</small>
      </div>
    </div>

    <p v-if="!temCampos" class="sem-campos">
      Este provedor não exige configuração adicional.
    </p>
  </div>
</template>

<script>
/*
 * Renderiza o formulário de uma integração a partir do manifesto do conector,
 * em vez de ter um formulário escrito para cada integração.
 *
 * É a evolução de views/Parametros/TipoInput.vue (bloco do tipo INTEGRATION),
 * que montava os campos a partir de config/formatos.php. A diferença é o que o
 * manifesto carrega junto: rótulo legível, texto de ajuda, obrigatoriedade,
 * marcação de segredo, agrupamento e condição de exibição. Isso é o que permite
 * publicar um conector novo sem tocar nesta tela.
 */

// Devolvida pela API no lugar de um segredo já preenchido. Reenviada sem
// alteração, faz o backend manter a credencial gravada.
const MASCARA_SEGREDO = '••••••••';

import { get, post } from '@/helpers/apiRequest';
import ConexaoHub from './ConexaoHub';

export default {
  name: 'FormularioManifesto',
  components: { ConexaoHub },
  props: {
    /*
     * Identificador da instância e estado da conta conectada. Vêm por prop
     * porque não são valores do formulário: quem os produz é o backend, e quem
     * os altera é a rota de autorização, não o salvar desta tela.
     */
    integracaoId: {
      type: [Number, String],
      default: null,
    },
    conectarAoAbrir: {
      type: String,
      default: '',
    },
    salvando: {
      type: Boolean,
      default: false,
    },
    // Conector a que este formulário pertence: é por ele que a tela pede ao
    // servidor o endereço já montado com o responsável escolhido.
    slug: {
      type: String,
      default: '',
    },
    /*
     * Um grupo por vez, para o assistente mostrar só o que a etapa pede. Com
     * `null`, a tela é a de sempre: todos os campos de uma vez.
     *
     * A ausência é `null` e não a string vazia porque o grupo sem nome é a
     * string vazia: tomada como ausência, a etapa dele desligava o filtro e
     * mostrava o formulário inteiro, repetindo campos entre etapas.
     */
    somenteGrupo: {
      type: String,
      default: null,
    },
    conexao: {
      type: Object,
      default: () => ({}),
    },
    // Definição do provedor escolhido, vinda de manifesto.provedores[].
    provedor: {
      type: Object,
      default: () => ({}),
    },
    // Valores atuais, no formato { chave: valor }.
    value: {
      type: Object,
      default: () => ({}),
    },
    // Erros de validação devolvidos pela API, no formato { chave: mensagem }.
    erros: {
      type: Object,
      default: () => ({}),
    },
    /*
     * Endereço que o parceiro chama para nos avisar. Quem gera é o backend, a
     * partir do token da instância — por isso vem por prop e não pelos valores
     * do formulário: não é dado que o usuário edita nem que é salvo daqui.
     */
    urlWebhook: {
      type: String,
      default: '',
    },
    // O que esse endereço recebe, declarado no manifesto do conector.
    eventosWebhook: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      segredoVisivel: {},
      copiado: false,
      // Listas que vêm do sistema, por fonte declarada no manifesto.
      opcoesRemotas: {},
      carregando: {},
      // Fontes cuja consulta falhou. Sem isso, a lista vazia por erro de rede
      // é indistinguível de "o sistema não tem nenhum", e o usuário conclui
      // que precisa cadastrar o que já existe.
      falhouFonte: {},
      // Endereço recalculado no servidor quando o responsável muda.
      enderecoCalculado: '',
    };
  },
  computed: {
    // Sim e Não também passam pelo mesmo componente: duas listas diferentes na
    // mesma tela, uma com busca e outra sem, destoam sem motivo.
    /*
     * O endereço que o usuário copia. O que veio com a tela vale até alguém
     * mexer no responsável; daí em diante vale o recalculado, porque o token
     * que viaja na URL é o de quem está escolhido agora.
     */
    enderecoExibido() {
      return this.enderecoCalculado || this.urlWebhook;
    },

    temCampoWebhook() {
      return this.campos.some((campo) => campo.tipo === 'WEBHOOK');
    },

    campos() {
      return (this.provedor && this.provedor.campos) || [];
    },

    temCampos() {
      return this.campos.length > 0;
    },

    /*
     * Um campo com `visivel_se` só aparece quando a condição é atendida. Isso
     * evita o formulário longo em que metade das opções não se aplica à escolha
     * que o usuário acabou de fazer.
     */
    camposVisiveis() {
      return this.campos.filter((campo) => this.condicaoAtendida(campo));
    },

    /*
     * Agrupa preservando a ordem em que os campos aparecem no manifesto: o
     * primeiro campo de cada grupo define onde o grupo entra na tela.
     */
    gruposVisiveis() {
      const grupos = [];

      this.camposVisiveis.forEach((campo) => {
        const nome = campo.grupo || '';
        let grupo = grupos.find((g) => g.nome === nome);

        if (!grupo) {
          grupo = { nome, campos: [] };
          grupos.push(grupo);
        }

        grupo.campos.push(campo);
      });

      return this.somenteGrupo === null
        ? grupos
        : grupos.filter((grupo) => grupo.nome === this.somenteGrupo);
    },
  },
  watch: {
    // Trocar de provedor troca os campos, e com eles as listas a buscar.
    provedor: {
      immediate: true,
      handler: 'carregarOpcoesRemotas',
    },
  },
  methods: {
    /**
     * Valores prontos que o conector oferece para este campo.
     *
     * Lista simples de strings no manifesto: o que se clica é exatamente o que
     * se grava, sem rótulo intermediário que faça o usuário adivinhar qual é
     * o valor real.
     */
    exemplosDoCampo(campo) {
      return Array.isArray(campo.exemplos) ? campo.exemplos : [];
    },

    valorDoCampo(campo) {
      const valor = this.value[campo.chave];

      if (valor === null || valor === undefined) {
        return '';
      }

      return valor;
    },

    valorBooleano(campo) {
      const valor = this.value[campo.chave];

      if (valor === null || valor === undefined) {
        return campo.padrao ? '1' : '0';
      }

      return valor === true || valor === 1 || valor === '1' ? '1' : '0';
    },

    /** O interruptor pergunta por verdadeiro/falso, não pelo texto '1'. */
    ligado(campo) {
      return this.valorBooleano(campo) === '1';
    },

    tipoDoInput(campo) {
      if (campo.tipo === 'NUMERIC') return 'number';
      if (campo.tipo === 'EMAIL') return 'email';
      if (campo.tipo === 'URL') return 'url';

      return 'text';
    },

    condicaoAtendida(campo) {
      const condicao = campo.visivel_se;

      if (!condicao || !condicao.campo) {
        return true;
      }

      const atual = this.normalizar(this.value[condicao.campo]);

      if (Object.prototype.hasOwnProperty.call(condicao, 'igual')) {
        return atual === this.normalizar(condicao.igual);
      }

      // "Depois de preencher aquele outro": o que importa é ter valor, não ter
      // um valor específico. Segredo já gravado volta mascarado, e a máscara
      // conta como preenchida — é o que ela significa.
      if (Object.prototype.hasOwnProperty.call(condicao, 'preenchido')) {
        return (atual !== '') === !!condicao.preenchido;
      }

      if (Array.isArray(condicao.em)) {
        return condicao.em.map(this.normalizar).includes(atual);
      }

      return true;
    },

    // O select de booleano devolve string; o manifesto declara true/false.
    normalizar(valor) {
      if (valor === true) return '1';
      if (valor === false) return '0';
      if (valor === null || valor === undefined) return '';

      return String(valor);
    },

    /**
     * As opções do campo: as declaradas no manifesto ou as que vieram do
     * sistema, quando o campo aponta uma fonte.
     */
    opcoesDoCampo(campo) {
      if (campo.fonte) {
        return this.opcoesRemotas[campo.fonte] || [];
      }

      return Object.keys(campo.opcoes || {}).map((valor) => ({
        valor,
        rotulo: campo.opcoes[valor],
      }));
    },

    /**
     * Nova tentativa para uma fonte que falhou. A carga ignora fonte já
     * buscada, então a consulta anterior sai do caminho antes de pedir de novo.
     */
    recarregarFonte(fonte) {
      this.$delete(this.opcoesRemotas, fonte);
      this.$set(this.falhouFonte, fonte, false);
      this.carregarOpcoesRemotas();
    },

    /**
     * O campo guarda credencial? O manifesto diz por `secreto`; o tipo senha
     * continua valendo sozinho para os conectores que não declaram a chave.
     */
    campoSecreto(campo) {
      return !!campo.secreto || campo.tipo === 'PASSWORD';
    },

    opcaoEscolhida(campo) {
      const valor = this.valorDoCampo(campo);

      return this.opcoesDoCampo(campo).find((opcao) => opcao.valor === String(valor)) || null;
    },

    escolher(campo, opcao) {
      this.atualizar(campo, opcao ? opcao.valor : '');

      /*
       * Trocar quem responde troca o token que viaja no endereço. Qual campo
       * tem esse efeito é declaração do manifesto — `colaborador_api` segue
       * aceito porque é o que os conectores já publicados usam, e esta tela
       * não pode exigir que todos sejam republicados para continuar valendo.
       */
      if (campo.recalcula_webhook || campo.fonte === 'colaborador_api') {
        this.$nextTick(() => this.recalcularEndereco());
      }
    },

    /**
     * Pede ao servidor o endereço como ele fica com o que está no formulário.
     *
     * O token de quem responde não vem para o navegador: é credencial de um
     * colaborador, e a lista traz só nome e identificador. Quem sabe montar a
     * URL é quem tem o token.
     */
    recalcularEndereco() {
      if (!this.temCampoWebhook || !this.slug) {
        return;
      }

      post(`/admin/integracoes/catalogo/${this.slug}/endereco-webhook`, {
        provedor: this.provedor && this.provedor.chave,
        valores: this.value,
        integracao_id: this.integracaoId || undefined,
      })
        .then((resposta) => {
          this.enderecoCalculado = (resposta.data && resposta.data.url) || '';
        })
        .catch(() => {
          // Sem o recálculo, continua valendo o endereço que veio com a tela.
        });
    },

    /**
     * Busca as listas que vêm do sistema. Uma vez por fonte: as mesmas opções
     * servem a todos os campos que as pedirem, e recarregá-las a cada campo
     * faria três requisições para mostrar três selects da mesma tabela.
     */
    carregarOpcoesRemotas() {
      const fontes = ((this.provedor && this.provedor.campos) || [])
        .map((campo) => campo.fonte)
        .filter((fonte) => fonte && !this.opcoesRemotas[fonte] && !this.carregando[fonte]);

      [...new Set(fontes)].forEach((fonte) => {
        this.$set(this.carregando, fonte, true);

        get(`/admin/integracoes/opcoes/${fonte}`)
          // A resposta vem envelopada em `data`; listagens de outros endpoints
          // vêm na raiz. Aceitar as duas evita depender de qual é qual.
          .then((resposta) => {
            this.$set(this.opcoesRemotas, fonte, resposta.data || resposta || []);
            this.$set(this.falhouFonte, fonte, false);
          })
          .catch(() => {
            this.$set(this.opcoesRemotas, fonte, []);
            this.$set(this.falhouFonte, fonte, true);
          })
          .finally(() => this.$set(this.carregando, fonte, false));
      });
    },

    atualizar(campo, valor) {
      this.$emit('input', { ...this.value, [campo.chave]: valor });
    },

    /*
     * Clicar no campo de senha limpa a máscara: sem isso o usuário editaria a
     * string de bolinhas e a enviaria como se fosse a senha nova.
     */
    limpaMascara(campo) {
      if (this.value[campo.chave] === MASCARA_SEGREDO) {
        this.atualizar(campo, '');
      }
    },

    alternarVisibilidade(campo) {
      this.$set(this.segredoVisivel, campo.chave, !this.segredoVisivel[campo.chave]);
    },

    /*
     * Copiar é a ação principal deste campo — o endereço vai ser colado no
     * painel do parceiro, e digitá-lo à mão erra.
     *
     * Copia o que está à vista, não o que veio com a tela: escolhido o
     * responsável, o endereço na tela ganha o token dele, e copiar o original
     * entregava ao parceiro uma URL que a autenticação recusa — sem nada que
     * ligasse a recusa a este botão.
     */
    copiarUrl() {
      const aviso = () => {
        this.copiado = true;
        setTimeout(() => {
          this.copiado = false;
        }, 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.enderecoExibido).then(aviso);
        return;
      }

      // Navegador sem a API de área de transferência (ou página fora de HTTPS):
      // o caminho antigo ainda funciona e evita deixar o usuário sem saída.
      const campo = document.createElement('textarea');
      campo.value = this.enderecoExibido;
      document.body.appendChild(campo);
      campo.select();
      document.execCommand('copy');
      document.body.removeChild(campo);
      aviso();
    },
  },
};
</script>

<style scoped>
.opcoes-falharam {
  color: #c0392b;
}

.opcoes-falharam a {
  margin-left: 6px;
  text-decoration: underline;
}

.campo-exemplos {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.campo-exemplos__rotulo {
  color: #6b7280;
  font-size: 11px;
}

.campo-exemplos__opcao {
  background: #f2f2f8;
  border: 1px solid #dddce9;
  border-radius: 6px;
  color: #3a3e81;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11.5px;
  padding: 3px 8px;
}

.campo-exemplos__opcao:hover {
  background: #eceaf3;
  border-color: #3a3e81;
}

.campo-exemplos__opcao--atual {
  background: #3a3e81;
  border-color: #3a3e81;
  color: #fff;
}

.grupo-campos + .grupo-campos {
  margin-top: 24px;
}

.grupo-titulo {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #667085;
  padding-bottom: 6px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 4px;
}

.obrigatorio {
  color: #c0392b;
}

.campo-secreto {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

/*
 * Liga/desliga em linha: o texto à esquerda, o interruptor à direita, e uma
 * régua separando um do outro. Sem a régua, uma lista de seis interruptores
 * vira um bloco cinza em que não se sabe qual explicação pertence a qual.
 */
.campo-interruptor {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f1f3;
}

.campo-interruptor:last-child {
  border-bottom: 0;
}

.campo-interruptor__texto {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.campo-interruptor__titulo {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.campo-interruptor__ajuda {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.45;
  color: #667085;
}

.interruptor {
  position: relative;
  flex: 0 0 auto;
  width: 40px;
  height: 22px;
  margin-top: 2px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #d0d5dd;
  cursor: pointer;
  transition: background 0.15s ease-in-out;
}

.interruptor:focus-visible {
  outline: 2px solid #3a3e81;
  outline-offset: 2px;
}

.interruptor--ligado {
  background: #2298a3;
}

.interruptor__bolinha {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.2);
  transition: transform 0.15s ease-in-out;
}

.interruptor--ligado .interruptor__bolinha {
  transform: translateX(18px);
}

.campo-ajuda {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}

.campo-erro {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #c0392b;
}

.campo-webhook__linha {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.campo-webhook__linha .form-control {
  background: #f8f9fa;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

/*
 * O botão acompanha a altura do campo. Com `size="sm"` ele ganhava padding
 * próprio e estourava a linha para cima e para baixo, ficando maior que o
 * input que ele serve.
 */
.campo-webhook__linha .btn {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 6px;
  height: auto;
  padding: 0 14px;
  white-space: nowrap;
}

/*
 * O campo de escolha precisa parecer irmão do campo de texto: o componente
 * traz altura e fonte próprias e, misturados no mesmo formulário, um ficava
 * três pixels mais baixo que o outro e com letra maior.
 *
 * 40px é a altura que o `.form-control` tem neste tema (medida na tela, não
 * deduzida da fórmula do Bootstrap, que o tema sobrescreve).
 */
.formulario-manifesto >>> .v-select .dropdown-toggle {
  align-items: center;
  display: flex;
  font-size: 0.7rem;
  height: 40px;
  padding: 0 10px;
}

.formulario-manifesto >>> .v-select .selected-tag,
.formulario-manifesto >>> .v-select input[type='search'] {
  font-size: 0.7rem;
  line-height: 1.4;
  margin: 0;
  padding: 0;
}

.formulario-manifesto >>> .v-select .dropdown-menu {
  font-size: 0.7rem;
}

.campo-webhook__pendente {
  margin: 0;
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  color: #667085;
  font-size: 12px;
}

.campo-webhook__eventos {
  margin: 8px 0 0;
  padding: 10px 12px 10px 28px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  font-size: 12px;
  color: #344054;
  line-height: 1.5;
}

.sem-campos {
  margin: 0;
  padding: 16px;
  background: #f8f9fa;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  color: #667085;
  font-size: 13px;
  text-align: center;
}
</style>
